import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, type InferProps, number, string } from 'prop-types'
import * as React from 'react'
import { useSelector } from 'react-redux'

import {
  SA_SELLER_ID,
  TENANT_AT,
  TENANT_BE,
  TENANT_COM,
  TENANT_FR,
  TENANT_IT,
} from 'shared/consts'
import type { IState } from 'types/redux'
import logoIconRebranding from 'views/assets/images/logos/logo-icon-IT-rebranding.svg'
import Image from 'views/components/atoms/Image'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import FreeShippingIndicatorAddressBox from 'views/components/organisms/FreeShippingIndicator/FreeShippingIndicatorAddressBox'
import { SELLER_MODAL_ID } from 'views/components/organisms/SellerModal/names'
import Tenant from 'views/components/utils/Tenant'
import { getSellerModalHook } from 'views/containers/browser/SellerModalContainer/clientsideHook'
import CswWithHydration from 'views/containers/spa/Cart/CswWithHydration'
import useMountPoints from 'views/enhancers/useMountPoints'
import { cartShippingDetail, sellerGroup } from 'views/utils/propTypes'
import { usePublicConfig } from 'views/utils/shopAndRuntimeConfigUtils'

import './SellerShippingAddressBox.scss'

const ShippingAddressBoxProps = {
  seller: string.isRequired,
  sellerId: string.isRequired,
  idx: number.isRequired,
  total: number,
  sellerGroups: arrayOf(sellerGroup),
  shippingDetail: arrayOf(cartShippingDetail),
  currency: string,
}

type PropTypes = InferProps<typeof ShippingAddressBoxProps>

const SellerShippingAddressBox: React.FunctionComponent<PropTypes> = ({
  idx,
  seller,
  sellerId,
  sellerGroups,
  shippingDetail,
}: PropTypes): React.ReactElement => {
  const { tenant } = usePublicConfig()
  const isRebrandingEnabled = tenant === TENANT_IT
  const mountPoints = useMountPoints()

  const shippingDetailForSeller = shippingDetail?.find(
    shippingDetailItem => shippingDetailItem?.sellerId === sellerId
  )
  const shippingCost = shippingDetailForSeller?.shippingCost
  const isFreeShipping = shippingCost === 0
  const isSellerShopApotheke = sellerId === SA_SELLER_ID

  const renderAllSellerProductsTenantList = [
    TENANT_COM,
    TENANT_AT,
    TENANT_IT,
    TENANT_BE,
  ]

  const shouldRenderShowAllProductsButton =
    !isSellerShopApotheke && renderAllSellerProductsTenantList.includes(tenant)

  const carrierInfoLink = (
    <ButtonAsLink
      name="sellerShippingAddressBoxModal"
      className="o-SellerShippingAddressBox__carrier-info-link"
      data-a11y-dialog-show="sellerShippingAddressBoxModal"
      isClientsideControlled
      neutral
    >
      <Trans id="general.label.show" />
    </ButtonAsLink>
  )

  const sellerShippingAddressBoxModal = (
    <div className="u-hidden@small-tiny">
      <span className="u-color--grey o-SellerShippingAddressBox__carrier-info-text">
        {!isRebrandingEnabled && (
          <>
            <Trans id="freeShippingIndicator.label.shippingCost" />{' '}
          </>
        )}
        {carrierInfoLink}
      </span>
    </div>
  )
  const cartInitialState = useSelector(
    (state: IState) => state.componentInitialState.CartComponentInitialState
  )

  return (
    <div className="a-box a-box--blue-light u-margin--bottom a-box--secondary-background o-SellerShippingAddressBox o-sellerShippingBox">
      <div className="l-flex l-flex--distribute-space l-flex--vertical-stretch@small-tiny o-SellerShippingAddressBox__box">
        <div className="l-flex u-padding-small--bottom@small-tiny o-SellerShippingAddressBox__shipment">
          <div className="l-flex o-SellerShippingAddressBox__shipment-by">
            {isRebrandingEnabled && (
              <Image
                className="o-SellerShippingAddressBox__shipment-by--logo"
                src={logoIconRebranding}
                alt="logoIconRebranding"
              />
            )}
            <div className="l-flex l-flex--vertical-stretch">
              <span
                className={classNames(
                  'a-h3 u-no-margin--bottom u-margin-x-small--right o-SellerShippingAddressBox__shipment-by--seller',
                  {
                    'o-sellerTextColor': !isSellerShopApotheke,
                  }
                )}
                data-qa-id="cart-sellerGroup-headline"
              >
                <Trans
                  id="freeShippingIndicator.label.deliveryCost"
                  values={{
                    seller: isSellerShopApotheke ? (
                      seller
                    ) : (
                      <span
                        data-a11y-dialog-show={SELLER_MODAL_ID}
                        data-clientside-hook={getSellerModalHook(sellerId)}
                        data-qa-id="seller-name"
                        data-target="seller-box"
                        className="a-link a-link--no-underline"
                      >
                        {seller}
                      </span>
                    ),
                  }}
                />
              </span>
              {isRebrandingEnabled &&
                isSellerShopApotheke &&
                !isFreeShipping &&
                sellerShippingAddressBoxModal}

              {shouldRenderShowAllProductsButton && (
                <a
                  href={mountPoints.getSellerProducts(sellerId)}
                  className="a-link--underlined u-no-margin u-font-weight--bold o-sellerProductsButton"
                >
                  <Trans id="seller.showAllProducts" />
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="l-flex l-flex--vertical-stretch@small-tiny">
          <FreeShippingIndicatorAddressBox
            shippingDetail={shippingDetail}
            sellerGroups={sellerGroups}
            idx={idx}
          />
        </div>
      </div>

      <Tenant tenant={[TENANT_BE, TENANT_FR, TENANT_IT]}>
        {!cartInitialState?.hideCartSuggestionCsw && (
          <CswWithHydration isFreeShipping={isFreeShipping} />
        )}
      </Tenant>
    </div>
  )
}

SellerShippingAddressBox.propTypes = ShippingAddressBoxProps

export default SellerShippingAddressBox
